import React, { useEffect, useState } from "react";
import {
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  getProductsDetails,
  getProductsPaginatedAndFiltered,
} from "@utils/api-communication/RestApiCli";
import { IProduct } from "types/productManagementTypes";

type Filters = {
  uuid?: string;
  version?: string;
  orgId?: string;
};

type SortOrder = "asc" | "desc";

const time = (value?: Date) => {
  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(parsed) ? null : parsed;
};

// /product/list has no sort support, so the page is ordered here; a missing
// vrDate sorts last either way.
export const sortByVrDate = (products: IProduct[], order: SortOrder): IProduct[] =>
  [...products].sort((a, b) => {
    const aTime = time(a.vrDate);
    const bTime = time(b.vrDate);
    if (aTime === null || bTime === null) {
      if (aTime === bTime) return 0;
      return aTime === null ? 1 : -1;
    }
    return order === "asc" ? aTime - bTime : bTime - aTime;
  });

// The count is in the response body, a level below the axios response.
export const parseProductsPage = (response: unknown): { products: IProduct[]; total: number } => {
  const body = (response as { data?: { products?: unknown; total?: unknown } })?.data ?? {};
  const products = Array.isArray(body.products) ? (body.products as IProduct[]) : [];
  return {
    products,
    total: typeof body.total === "number" ? body.total : products.length,
  };
};

const ProductsList: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [serverTotal, setServerTotal] = useState(0);
  // The org endpoint returns everything at once, so that branch pages here.
  const [clientPaged, setClientPaged] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [filters, setFilters] = useState<Filters>({});

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchProducts = () => {
    setLoading(true);

    if (filters.orgId) {
      getProductsDetails(
        (response) => {
          const items = Array.isArray(response.data) ? response.data : [];
          const filteredItems = items.filter(
            (product: IProduct) =>
              (!filters.version || product.version?.includes(filters.version)) &&
              (!filters.uuid || product.uuid?.includes(filters.uuid)),
          );
          setProducts(filteredItems);
          setClientPaged(true);
          setLoading(false);
        },
        (error) => {
          console.error("Failed to fetch products by orgId", error);
          setProducts([]);
          setClientPaged(true);
          setLoading(false);
        },
        filters.orgId,
      );
      return;
    }

    const filterObject: Record<string, string> = {};
    if (filters.version) filterObject.version = filters.version;
    if (filters.uuid) filterObject.uuid = filters.uuid;

    getProductsPaginatedAndFiltered(
      limit,
      currentPage,
      Object.keys(filterObject).length !== 0 ? JSON.stringify(filterObject) : null,
      (response) => {
        const { products: items, total } = parseProductsPage(response);
        setProducts(items);
        setServerTotal(total);
        setClientPaged(false);
        setLoading(false);
      },
      (error) => {
        console.error("Failed to fetch products", error);
        setProducts([]);
        setServerTotal(0);
        setClientPaged(false);
        setLoading(false);
      },
    );
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const applyOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") fetchProducts();
  };

  const openProductStatus = (product: IProduct) => {
    navigate(`/product-status?pid=${encodeURIComponent(product.uuid)}`, {
      state: { from: "/product-management?tab=products", fromLabel: "Product Management" },
    });
  };

  const sorted = sortByVrDate(products, sortOrder);
  const total = clientPaged ? sorted.length : serverTotal;
  const rows = clientPaged ? sorted.slice((currentPage - 1) * limit, currentPage * limit) : sorted;
  const initialLoading = loading && products.length === 0;

  return (
    <>
      <Group mb="md">
        <TextInput
          placeholder="UUID"
          value={filters.uuid || ""}
          onChange={(e) => handleFilterChange("uuid", e.currentTarget.value)}
          onKeyDown={applyOnEnter}
        />
        <TextInput
          placeholder="Version"
          value={filters.version || ""}
          onChange={(e) => handleFilterChange("version", e.currentTarget.value)}
          onKeyDown={applyOnEnter}
        />
        <TextInput
          placeholder="Organization ID"
          value={filters.orgId || ""}
          onChange={(e) => handleFilterChange("orgId", e.currentTarget.value)}
          onKeyDown={applyOnEnter}
        />
        <Button onClick={fetchProducts}>Apply Filters</Button>
        <Button variant="outline" onClick={clearFilters}>
          Clear Filters
        </Button>
      </Group>

      {initialLoading ? (
        <Center style={{ height: "200px" }}>
          <Loader />
        </Center>
      ) : (
        <>
          <Paper withBorder radius="sm" p={0}>
            <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>UUID</Table.Th>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>
                    <UnstyledButton
                      onClick={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
                      data-testid="products-vrdate-sort"
                    >
                      <Group gap={4} wrap="nowrap">
                        <Text size="sm" fw={700}>
                          VR Date
                        </Text>
                        {sortOrder === "desc" ? (
                          <IconArrowDown size={14} />
                        ) : (
                          <IconArrowUp size={14} />
                        )}
                      </Group>
                    </UnstyledButton>
                  </Table.Th>
                  <Table.Th>Last Access</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="sm">
                        No products found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows.map((product, idx) => (
                    <Table.Tr
                      key={product.uuid}
                      onClick={() => openProductStatus(product)}
                      style={{ cursor: "pointer" }}
                    >
                      <Table.Td>{(currentPage - 1) * limit + idx + 1}</Table.Td>
                      <Table.Td>{product.uuid || "N/A"}</Table.Td>
                      <Table.Td>{product.version || "N/A"}</Table.Td>
                      <Table.Td>
                        {product.vrDate ? new Date(product.vrDate).toLocaleString() : "N/A"}
                      </Table.Td>
                      <Table.Td>
                        {product.lastAccess ? new Date(product.lastAccess).toLocaleString() : "N/A"}
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>

          <Pagination
            value={currentPage}
            onChange={setCurrentPage}
            total={Math.max(1, Math.ceil(total / limit))}
            withControls
            withEdges
            mt="md"
          />
        </>
      )}
    </>
  );
};

export default ProductsList;
